import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Send, Search, Gamepad2, DollarSign, User, Clock, 
  Check, X, Mail, ChevronRight, ChevronLeft, Target, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChallengePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('send');
  const [games, setGames] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [receivedInvites, setReceivedInvites] = useState([]);
  const [sentInvites, setSentInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Challenge form
  const [step, setStep] = useState(1);
  const [selectedOpponent, setSelectedOpponent] = useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [entryAmount, setEntryAmount] = useState('');
  const [config, setConfig] = useState(null);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [gamesRes, receivedRes, sentRes, configRes] = await Promise.all([
        axios.get(`${API}/matches/games`),
        axios.get(`${API}/matches/invites/received`, { withCredentials: true }),
        axios.get(`${API}/matches/invites/sent`, { withCredentials: true }),
        axios.get(`${API}/matches/config`, { withCredentials: true })
      ]);
      setGames(gamesRes.data);
      setReceivedInvites(receivedRes.data);
      setSentInvites(sentRes.data);
      setConfig(configRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchQuery.length < 2) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/profile/search?q=${encodeURIComponent(searchQuery)}`, {
        withCredentials: true
      });
      setSearchResults(response.data.filter(u => u.user_id !== user?.user_id));
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const handleSendChallenge = async () => {
    if (!selectedOpponent || !selectedGame || !entryAmount) {
      toast.error('Please complete all fields');
      return;
    }
    
    const amount = parseFloat(entryAmount);
    const rate = config?.credit_conversion_rate || 100;
    const entryCredits = amount * rate;
    if (entryCredits > (user?.credits_balance || 0)) {
      toast.error('Insufficient credits');
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/matches/invite`,
        {
          game_id: selectedGame.id,
          wager_amount: amount,
          opponent_email: selectedOpponent.email,
          is_custom_game: false
        },
        { withCredentials: true }
      );
      
      toast.success(`Challenge sent to ${selectedOpponent.name}!`);
      await refreshUser();
      navigate(`/match/${response.data.match_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send challenge');
    } finally {
      setSending(false);
    }
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      const response = await axios.post(
        `${API}/matches/invites/${inviteId}/accept`,
        {},
        { withCredentials: true }
      );
      toast.success('Challenge accepted!');
      await refreshUser();
      navigate(`/match/${response.data.match_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      await axios.post(
        `${API}/matches/invites/${inviteId}/decline`,
        {},
        { withCredentials: true }
      );
      toast.success('Challenge declined');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline');
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const quickAmounts = [5, 10, 25, 50, 100];

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="challenge-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Challenge Players</h1>
          <p className="text-white/50">Send direct challenges or respond to invites</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'send' ? 'default' : 'outline'}
            onClick={() => setActiveTab('send')}
            className={activeTab === 'send' ? 'btn-primary' : 'btn-secondary'}
          >
            <Send className="w-4 h-4 mr-2" />
            Send Challenge
          </Button>
          <Button
            variant={activeTab === 'received' ? 'default' : 'outline'}
            onClick={() => setActiveTab('received')}
            className={activeTab === 'received' ? 'btn-primary' : 'btn-secondary'}
          >
            <Mail className="w-4 h-4 mr-2" />
            Received
            {receivedInvites.length > 0 && (
              <span className="ml-2 px-2 py-0.5 rounded-full bg-[#FF2E2E] text-white text-xs">
                {receivedInvites.length}
              </span>
            )}
          </Button>
          <Button
            variant={activeTab === 'sent' ? 'default' : 'outline'}
            onClick={() => setActiveTab('sent')}
            className={activeTab === 'sent' ? 'btn-primary' : 'btn-secondary'}
          >
            <Clock className="w-4 h-4 mr-2" />
            Sent
          </Button>
        </div>

        {/* Send Challenge Tab */}
        {activeTab === 'send' && (
          <motion.div
            key="send"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="glass-card rounded-2xl p-8"
          >
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-4 mb-8">
              {[
                { num: 1, label: 'Find Player', icon: Search },
                { num: 2, label: 'Select Game', icon: Gamepad2 },
                { num: 3, label: 'Set Entry Fee', icon: DollarSign },
              ].map((s, i) => (
                <div key={s.num} className="flex items-center">
                  <div 
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                      step === s.num 
                        ? 'bg-[#00F0FF]/20 text-[#00F0FF]' 
                        : step > s.num
                          ? 'bg-[#00FF94]/20 text-[#00FF94]'
                          : 'bg-white/5 text-white/40'
                    }`}
                  >
                    {step > s.num ? <Check className="w-4 h-4" /> : <s.icon className="w-4 h-4" />}
                    <span className="text-xs font-mono uppercase tracking-wider hidden sm:inline">{s.label}</span>
                  </div>
                  {i < 2 && <ChevronRight className="w-4 h-4 text-white/20 mx-2" />}
                </div>
              ))}
            </div>

            {/* Step 1: Find Player */}
            {step === 1 && (
              <div>
                <h2 className="font-heading font-semibold text-xl mb-6">Find a Player to Challenge</h2>
                
                <div className="flex gap-3 mb-6">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                    <Input
                      placeholder="Search by name or email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-10 input-dark"
                      data-testid="player-search-input"
                    />
                  </div>
                  <Button onClick={handleSearch} disabled={searching} className="btn-primary">
                    {searching ? 'Searching...' : 'Search'}
                  </Button>
                </div>

                {searchResults.length > 0 && (
                  <div className="space-y-3">
                    {searchResults.map(player => (
                      <motion.div
                        key={player.user_id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        onClick={() => {
                          setSelectedOpponent(player);
                          setStep(2);
                        }}
                        className={`flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all ${
                          selectedOpponent?.user_id === player.user_id
                            ? 'bg-[#00F0FF]/20 border border-[#00F0FF]'
                            : 'bg-white/5 hover:bg-white/10'
                        }`}
                        data-testid={`player-${player.user_id}`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={player.picture} />
                            <AvatarFallback className="bg-[#7000FF] text-white">
                              {player.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{player.name}</p>
                            <p className="text-sm text-white/40">{player.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm">
                            <span className="text-[#00FF94]">{player.wins || 0}W</span>
                            <span className="text-white/30 mx-1">/</span>
                            <span className="text-[#FF2E2E]">{player.losses || 0}L</span>
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}

                {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
                  <div className="text-center py-8 text-white/50">
                    No players found matching "{searchQuery}"
                  </div>
                )}
              </div>
            )}

            {/* Step 2: Select Game */}
            {step === 2 && (
              <div>
                <h2 className="font-heading font-semibold text-xl mb-2">Select Game</h2>
                <p className="text-white/50 mb-6">
                  Challenging: <span className="text-[#00F0FF]">{selectedOpponent?.name}</span>
                </p>

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {games.map(game => (
                    <motion.div
                      key={game.id}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => {
                        setSelectedGame(game);
                        setStep(3);
                      }}
                      className={`p-4 rounded-xl cursor-pointer transition-all ${
                        selectedGame?.id === game.id
                          ? 'bg-[#7000FF]/20 border-2 border-[#7000FF]'
                          : 'bg-white/5 border-2 border-transparent hover:border-white/20'
                      }`}
                      data-testid={`game-${game.id}`}
                    >
                      <Gamepad2 className={`w-8 h-8 mx-auto mb-2 ${
                        selectedGame?.id === game.id ? 'text-[#7000FF]' : 'text-white/50'
                      }`} />
                      <p className="text-center text-sm font-medium">{game.name}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 3: Set Entry Fee */}
            {step === 3 && (
              <div>
                <h2 className="font-heading font-semibold text-xl mb-2">Set Entry Fee</h2>
                <p className="text-white/50 mb-6">
                  {selectedOpponent?.name} • <span className="text-[#7000FF]">{selectedGame?.name}</span>
                </p>

                <div className="mb-6">
                  <label className="text-sm text-white/50 mb-2 block">Entry Fee Amount (USD)</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-white/50 font-mono text-lg">$</span>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={entryAmount}
                      onChange={(e) => setEntryAmount(e.target.value)}
                      className="pl-10 text-2xl font-mono input-dark h-14"
                      min="1"
                      max="500"
                      data-testid="entry-fee-input"
                    />
                  </div>
                </div>

                {entryAmount && parseFloat(entryAmount) > 0 && (
                  <div className="p-3 rounded-lg bg-[#00F0FF]/10 border border-[#00F0FF]/20 flex items-center justify-between mb-4">
                    <span className="text-sm text-white/70">Competition Access:</span>
                    <span className="font-mono font-bold text-[#00F0FF]">
                      {(parseFloat(entryAmount) * (config?.credit_conversion_rate || 100)).toLocaleString()} credits
                    </span>
                  </div>
                )}

                <div className="flex flex-wrap gap-2 mb-6">
                  {quickAmounts.map(amount => (
                    <Button
                      key={amount}
                      variant="outline"
                      size="sm"
                      onClick={() => setEntryAmount(amount.toString())}
                      disabled={(user?.credits_balance || 0) < amount * (config?.credit_conversion_rate || 100)}
                      className={`btn-secondary ${entryAmount === amount.toString() ? 'border-[#00F0FF] bg-[#00F0FF]/10 text-[#00F0FF]' : ''}`}
                    >
                      ${amount} → {(amount * (config?.credit_conversion_rate || 100)).toLocaleString()}
                    </Button>
                  ))}
                </div>

                <div className="p-4 rounded-xl bg-white/5 mb-6">
                  <div className="flex justify-between mb-2">
                    <span className="text-white/50">Your Credits</span>
                    <span className="font-mono text-[#00F0FF]">{(user?.credits_balance || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-white/50">Competition Access</span>
                    <span className="font-mono text-white">{(parseFloat(entryAmount || 0) * (config?.credit_conversion_rate || 100)).toLocaleString()} credits</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-white/50">Winner Reward</span>
                    <span className="font-mono text-[#CCFF00]">
                      ${((parseFloat(entryAmount || 0) * 2) * 0.92).toFixed(2)}
                      <span className="text-xs text-[#00FF94] ml-1">(Funded by MatchStake)</span>
                    </span>
                  </div>
                </div>

                {parseFloat(entryAmount) * (config?.credit_conversion_rate || 100) > (user?.credits_balance || 0) && (
                  <div className="p-3 rounded-lg bg-[#FF2E2E]/10 flex items-center gap-2 mb-6">
                    <AlertCircle className="w-4 h-4 text-[#FF2E2E]" />
                    <span className="text-sm text-[#FF2E2E]">Insufficient credits</span>
                  </div>
                )}
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-white/10">
              {step > 1 ? (
                <Button variant="outline" onClick={() => setStep(step - 1)} className="btn-secondary">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
              ) : (
                <div />
              )}

              {step < 3 ? (
                <Button
                  onClick={() => setStep(step + 1)}
                  disabled={step === 1 && !selectedOpponent}
                  className="btn-primary clip-slant-sm"
                >
                  Continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSendChallenge}
                  disabled={sending || !entryAmount || parseFloat(entryAmount) * (config?.credit_conversion_rate || 100) > (user?.credits_balance || 0)}
                  className="btn-primary clip-slant"
                  data-testid="send-challenge-btn"
                >
                  {sending ? 'Sending...' : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Send Challenge
                    </>
                  )}
                </Button>
              )}
            </div>
          </motion.div>
        )}

        {/* Received Invites Tab */}
        {activeTab === 'received' && (
          <motion.div
            key="received"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : receivedInvites.length === 0 ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <Mail className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h2 className="font-heading font-semibold text-xl mb-2">No Pending Challenges</h2>
                <p className="text-white/50">You don't have any pending challenge invites</p>
              </div>
            ) : (
              <div className="space-y-4">
                {receivedInvites.map(invite => (
                  <motion.div
                    key={invite.invite_id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="glass-card rounded-xl p-5"
                    data-testid={`invite-${invite.invite_id}`}
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-[#FF2E2E]/20 flex items-center justify-center">
                          <Target className="w-6 h-6 text-[#FF2E2E]" />
                        </div>
                        <div>
                          <p className="font-semibold">{invite.from_user_name} challenges you!</p>
                          <p className="text-white/50">{invite.game_name}</p>
                          <p className="text-xs text-white/30">{formatTimeAgo(invite.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <p className="font-mono text-2xl font-bold text-[#00F0FF]">
                          {(invite.wager_amount * (config?.credit_conversion_rate || 100)).toLocaleString()} credits
                        </p>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleAcceptInvite(invite.invite_id)}
                            disabled={(user?.credits_balance || 0) < invite.wager_amount * (config?.credit_conversion_rate || 100)}
                            className="bg-[#00FF94] text-black hover:bg-[#00FF94]/90"
                            data-testid={`accept-${invite.invite_id}`}
                          >
                            <Check className="w-4 h-4 mr-1" /> Accept
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleDeclineInvite(invite.invite_id)}
                            className="border-[#FF2E2E]/30 text-[#FF2E2E] hover:bg-[#FF2E2E]/10"
                            data-testid={`decline-${invite.invite_id}`}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    {(user?.credits_balance || 0) < invite.wager_amount * (config?.credit_conversion_rate || 100) && (
                      <p className="text-xs text-[#FFB800] mt-2">
                        You need {((invite.wager_amount * (config?.credit_conversion_rate || 100)) - (user?.credits_balance || 0)).toLocaleString()} more credits to accept
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Sent Invites Tab */}
        {activeTab === 'sent' && (
          <motion.div
            key="sent"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />
                ))}
              </div>
            ) : sentInvites.length === 0 ? (
              <div className="glass-card rounded-xl p-12 text-center">
                <Send className="w-16 h-16 text-white/20 mx-auto mb-4" />
                <h2 className="font-heading font-semibold text-xl mb-2">No Sent Challenges</h2>
                <p className="text-white/50">You haven't sent any challenges yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sentInvites.map(invite => (
                  <div
                    key={invite.invite_id}
                    className="glass-card rounded-xl p-5"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-[#7000FF]/20 flex items-center justify-center">
                          <Gamepad2 className="w-5 h-5 text-[#7000FF]" />
                        </div>
                        <div>
                          <p className="font-medium">Challenge to {invite.to_user_email}</p>
                          <p className="text-sm text-white/50">{invite.game_name}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-mono font-bold text-[#00F0FF]">{(invite.wager_amount * (config?.credit_conversion_rate || 100)).toLocaleString()} credits</p>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          invite.status === 'pending' ? 'bg-[#FFB800]/20 text-[#FFB800]' :
                          invite.status === 'accepted' ? 'bg-[#00FF94]/20 text-[#00FF94]' :
                          'bg-white/10 text-white/50'
                        }`}>
                          {invite.status}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ChallengePage;
